import type { TrackerKpi, Week } from 'chery-contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { editorText, parseCellInput } from './tracker-display';
import {
  applyEdit,
  applyPaste,
  cellKey,
  editCount,
  effectiveRecord,
  EMPTY_EDIT_STATE,
  invalidKeys,
  isInSelection,
  movePosition,
  parseClipboard,
  pasteTargets,
  selectionBounds,
  splitCellKey,
  undo as undoStep,
  type CellEdit,
  type CellRecord,
  type CellWrite,
  type EditState,
  type GridPosition,
} from './tracker-state';

/**
 * Keyboard and mouse wiring of the tracker grid (plan §3.1, §7) over the pure state of `tracker-state.ts`:
 * click selects (shift-click or drag extends), double-click / Enter / typing edits, Tab and Enter commit and move,
 * a tab-delimited paste fills a block, Delete clears, Ctrl/⌘+Z undoes one action, Escape lets go. Read-only cells
 * (a VIEW pillar, a calculated KPI) can be selected and totalled but never enter editing. While a cell is being
 * edited, the text typed so far is exposed as `draftInput` so calculated cells recalculate on every keystroke.
 */

/** The open editor's current text, before it is committed. */
export interface DraftInput extends CellRef {
  raw: string;
}

export interface CellRef {
  kpiId: string;
  weekId: string;
}

export interface EditingCell extends CellRef {
  /** The editor's starting text: the typed character, or `undefined` for the cell's own value. */
  seed?: string;
}

export interface TrackerEditingArgs {
  /** KPI rows as navigated, top to bottom (collapsed pillars excluded). */
  rows: readonly TrackerKpi[];
  weeks: readonly Week[];
  baseOf: (kpiId: string, weekId: string) => CellRecord | null;
  /** False while a dialog or the comment panel owns the keyboard. */
  keyboardEnabled: boolean;
  onMessage: (text: string) => void;
}

export interface SaveEdit {
  kpiId: string;
  weekId: string;
  value: number | string | null;
  comment: string | null;
  reason: string | null;
  followUp: string | null;
}

const isFormField = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName) || target.isContentEditable);

const sameRef = (a: CellRef | null, b: CellRef | null): boolean => a !== null && b !== null && a.kpiId === b.kpiId && a.weekId === b.weekId;

export const useTrackerEditing = ({ rows, weeks, baseOf, keyboardEnabled, onMessage }: TrackerEditingArgs) => {
  const [state, setState] = useState<EditState>(EMPTY_EDIT_STATE);
  const [anchor, setAnchor] = useState<CellRef | null>(null);
  const [focus, setFocus] = useState<CellRef | null>(null);
  const [editing, setEditingState] = useState<EditingCell | null>(null);
  const [draftInput, setDraftInput] = useState<DraftInput | null>(null);
  const dragging = useRef(false);

  // Every way out of (or into) an editor drops the previous draft, so a stale draft never feeds a formula.
  const setEditing = useCallback((next: EditingCell | null): void => {
    setEditingState(next);
    setDraftInput(null);
  }, []);

  const onDraft = useCallback((kpiId: string, weekId: string, raw: string): void => setDraftInput({ kpiId, weekId, raw }), []);

  const rowIndex = useMemo(() => new Map(rows.map((kpi, index) => [kpi.id, index])), [rows]);
  const colIndex = useMemo(() => new Map(weeks.map((week, index) => [week.id, index])), [weeks]);
  const kpiById = useMemo(() => new Map(rows.map((kpi) => [kpi.id, kpi])), [rows]);

  const positionOf = useCallback(
    (ref: CellRef | null): GridPosition | null => (ref ? { row: rowIndex.get(ref.kpiId) ?? -1, col: colIndex.get(ref.weekId) ?? -1 } : null),
    [rowIndex, colIndex],
  );
  const refAt = useCallback(
    (position: GridPosition): CellRef | null => {
      const kpi = rows[position.row];
      const week = weeks[position.col];

      return kpi && week ? { kpiId: kpi.id, weekId: week.id } : null;
    },
    [rows, weeks],
  );

  const bounds = useMemo(() => selectionBounds(positionOf(anchor), positionOf(focus)), [anchor, focus, positionOf]);

  const isEditable = useCallback((kpiId: string): boolean => kpiById.get(kpiId)?.editable === true, [kpiById]);

  const recordAt = useCallback(
    (kpiId: string, weekId: string): CellRecord => effectiveRecord(baseOf(kpiId, weekId), state.edits[cellKey(kpiId, weekId)]),
    [baseOf, state.edits],
  );

  const editAt = useCallback((kpiId: string, weekId: string): CellEdit | undefined => state.edits[cellKey(kpiId, weekId)], [state.edits]);

  // The latest values for the window listeners, without re-subscribing them on every render.
  const live = useRef({ state, anchor, focus, editing, bounds, rows, weeks, keyboardEnabled });
  live.current = { state, anchor, focus, editing, bounds, rows, weeks, keyboardEnabled };

  const selectCell = useCallback((ref: CellRef, extend = false): void => {
    setFocus(ref);
    setAnchor((current) => (extend && current ? current : ref));
  }, []);

  const beginDrag = useCallback(
    (ref: CellRef, extend: boolean): void => {
      dragging.current = true;
      selectCell(ref, extend);
    },
    [selectCell],
  );

  const dragOver = useCallback((ref: CellRef): void => {
    if (dragging.current) {
      setFocus(ref);
    }
  }, []);

  const clearSelection = useCallback((): void => {
    setAnchor(null);
    setFocus(null);
  }, []);

  const startEdit = useCallback(
    (ref: CellRef, seed?: string): void => {
      setAnchor(ref);
      setFocus(ref);

      if (isEditable(ref.kpiId)) {
        setEditing({ ...ref, seed });
      }
    },
    [isEditable, setEditing],
  );

  const cancelEdit = useCallback((): void => setEditing(null), [setEditing]);

  /** Stages what the editor holds as one step, then moves (Enter: down, Tab: right, Shift+Tab: left). */
  const commitEdit = useCallback(
    (raw: string, move?: GridPosition): void => {
      const current = live.current.editing;

      if (!current) {
        return;
      }

      const kpi = kpiById.get(current.kpiId);

      if (kpi?.editable) {
        const key = cellKey(current.kpiId, current.weekId);
        const edit = live.current.state.edits[key];
        const before = editorText(effectiveRecord(baseOf(current.kpiId, current.weekId), edit).value, edit?.invalid);

        // Leaving a cell untouched is not an action, so it adds no Undo step.
        if (raw !== before || current.seed !== undefined) {
          setState((previous) => applyEdit(previous, key, parseCellInput(kpi.unit, raw), baseOf(current.kpiId, current.weekId)));
        }
      }

      setEditing(null);

      if (!move) {
        return;
      }

      const from = positionOf(current) as GridPosition;
      const next = refAt(movePosition(from, move, live.current.rows.length, live.current.weeks.length));

      if (next) {
        setAnchor(next);
        setFocus(next);

        if (isEditable(next.kpiId) && !sameRef(next, current)) {
          setEditing(next);
        }
      }
    },
    [baseOf, isEditable, kpiById, positionOf, refAt, setEditing],
  );

  /** Fills a tab-delimited block from `at` (default: the selection's top-left) as one step. */
  const pasteBlock = useCallback(
    (text: string, at?: CellRef): void => {
      const { bounds: currentBounds } = live.current;
      const start = at ? positionOf(at) : currentBounds ? { row: currentBounds.top, col: currentBounds.left } : null;

      if (!start || start.row < 0 || start.col < 0) {
        return;
      }

      const writes: CellWrite[] = [];
      let skipped = 0;

      for (const { row, col, raw } of pasteTargets(parseClipboard(text), start, live.current.rows.length, live.current.weeks.length)) {
        const kpi = live.current.rows[row] as TrackerKpi;
        const week = live.current.weeks[col] as Week;

        // A blank in the block never erases a value, and a read-only cell is never written.
        if (!kpi.editable || raw.trim() === '') {
          skipped += 1;
          continue;
        }

        writes.push({ key: cellKey(kpi.id, week.id), patch: parseCellInput(kpi.unit, raw), base: baseOf(kpi.id, week.id) });
      }

      setEditing(null);
      setState((previous) => applyPaste(previous, writes));
      onMessage(
        `${writes.length} pasted value${writes.length === 1 ? '' : 's'} staged — press Save to commit.${skipped > 0 ? ` ${skipped} blank or read-only cell${skipped === 1 ? '' : 's'} skipped.` : ''}`,
      );
    },
    [baseOf, onMessage, positionOf, setEditing],
  );

  /** Delete / Backspace on a selection: clears every editable cell in it, as one step. */
  const clearSelectedValues = useCallback((): void => {
    const { bounds: currentBounds } = live.current;

    if (!currentBounds) {
      return;
    }

    const writes: CellWrite[] = [];

    for (let row = currentBounds.top; row <= currentBounds.bottom; row += 1) {
      for (let col = currentBounds.left; col <= currentBounds.right; col += 1) {
        const ref = refAt({ row, col });

        if (ref && isEditable(ref.kpiId)) {
          writes.push({ key: cellKey(ref.kpiId, ref.weekId), patch: { value: null }, base: baseOf(ref.kpiId, ref.weekId) });
        }
      }
    }

    setState((previous) => applyPaste(previous, writes));
  }, [baseOf, isEditable, refAt]);

  /** Comment panel: the notes of one cell, staged as one step. */
  const stageNotes = useCallback(
    (ref: CellRef, notes: Pick<CellEdit, 'comment' | 'reason' | 'followUp'>): void => {
      if (isEditable(ref.kpiId)) {
        setState((previous) => applyEdit(previous, cellKey(ref.kpiId, ref.weekId), notes, baseOf(ref.kpiId, ref.weekId)));
      }
    },
    [baseOf, isEditable],
  );

  const stageClear = useCallback(
    (ref: CellRef): void => {
      if (isEditable(ref.kpiId)) {
        setState((previous) => applyEdit(previous, cellKey(ref.kpiId, ref.weekId), { value: null }, baseOf(ref.kpiId, ref.weekId)));
      }
    },
    [baseOf, isEditable],
  );

  const undo = useCallback((): void => {
    setEditing(null);
    setState(undoStep);
  }, [setEditing]);

  /** Cancel (and a discarded range change or navigation): every unsaved edit goes, the saved cells show again. */
  const discardAll = useCallback((): void => {
    setEditing(null);
    setState(EMPTY_EDIT_STATE);
  }, [setEditing]);

  /** The full record of every edited cell, for `tracker.saveValues`. */
  const savePayload = useCallback(
    (): SaveEdit[] =>
      Object.keys(state.edits).map((key) => {
        const { kpiId, weekId } = splitCellKey(key);
        const record = effectiveRecord(baseOf(kpiId, weekId), state.edits[key]);

        return { kpiId, weekId, value: record.value, comment: record.comment, reason: record.reason, followUp: record.followUp };
      }),
    [baseOf, state.edits],
  );

  useEffect(() => {
    const onMouseUp = (): void => {
      dragging.current = false;
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      const current = live.current;

      if (!current.keyboardEnabled || current.editing || isFormField(event.target)) {
        return;
      }

      const modifier = event.ctrlKey || event.metaKey;

      if (modifier && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undo();

        return;
      }

      if (!current.focus) {
        return;
      }

      const position = positionOf(current.focus);

      if (!position || position.row < 0 || position.col < 0) {
        return;
      }

      const arrows: Record<string, GridPosition> = {
        ArrowUp: { row: -1, col: 0 },
        ArrowDown: { row: 1, col: 0 },
        ArrowLeft: { row: 0, col: -1 },
        ArrowRight: { row: 0, col: 1 },
      };
      const arrow = arrows[event.key];

      if (arrow) {
        event.preventDefault();

        const next = refAt(movePosition(position, arrow, current.rows.length, current.weeks.length));

        if (next) {
          selectCell(next, event.shiftKey);
        }

        return;
      }

      if (event.key === 'Escape') {
        clearSelection();

        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        clearSelectedValues();

        return;
      }

      const single = sameRef(current.anchor, current.focus);

      if (event.key === 'Enter' || event.key === 'F2') {
        event.preventDefault();
        startEdit(current.focus);

        return;
      }

      if (single && event.key.length === 1 && !modifier && !event.altKey) {
        event.preventDefault();
        startEdit(current.focus, event.key);
      }
    };

    const onPaste = (event: ClipboardEvent): void => {
      const current = live.current;

      if (!current.keyboardEnabled || current.editing || isFormField(event.target) || !current.focus) {
        return;
      }

      const text = event.clipboardData?.getData('text/plain') ?? '';

      if (text !== '') {
        event.preventDefault();
        pasteBlock(text);
      }
    };

    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('paste', onPaste);

    return () => {
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('paste', onPaste);
    };
  }, [clearSelectedValues, clearSelection, pasteBlock, positionOf, refAt, selectCell, startEdit, undo]);

  const isSelected = useCallback(
    (kpiId: string, weekId: string): boolean => isInSelection(bounds, rowIndex.get(kpiId) ?? -1, colIndex.get(weekId) ?? -1),
    [bounds, rowIndex, colIndex],
  );

  return {
    state,
    edits: state.edits,
    unsavedCount: editCount(state),
    invalidCount: invalidKeys(state).length,
    canUndo: state.history.length > 0,
    anchor,
    focus,
    editing,
    draftInput,
    onDraft,
    bounds,
    isSelected,
    recordAt,
    editAt,
    selectCell,
    beginDrag,
    dragOver,
    clearSelection,
    startEdit,
    cancelEdit,
    commitEdit,
    pasteBlock,
    stageNotes,
    stageClear,
    undo,
    discardAll,
    savePayload,
  };
};

export type TrackerEditing = ReturnType<typeof useTrackerEditing>;
